import { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import type { Order, OrderStatus } from '../types';

const STATUS_OPTIONS: OrderStatus[] = ['Sent', 'Processing', 'Completed', 'Failed', 'Cancelled'];

const statusColorMap: Record<OrderStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  Sent: 'default',
  Processing: 'info',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface Props {
  orders: Order[];
  loading?: boolean;
  onSelectOrder: (orderId: string) => void;
  selectedOrderId?: string | null;
}

export default function OrderList({
  orders,
  loading = false,
  onSelectOrder,
  selectedOrderId,
}: Props) {
  const [searchText, setSearchText] = useState('');
  const [statusFilters, setStatusFilters] = useState<OrderStatus[]>([]);

  function toggleStatus(status: OrderStatus) {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  }

  const filtered = orders.filter((o) => {
    if (statusFilters.length > 0 && !statusFilters.includes(o.status)) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (
        o.orderId.toLowerCase().includes(q) ||
        (o.name?.toLowerCase().includes(q) ?? false) ||
        o.createdByLabel.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const columns: DataTableColumn<Order>[] = [
    {
      id: 'orderId',
      label: 'Order',
      width: 100,
      render: (row) => row.orderId,
    },
    {
      id: 'name',
      label: 'Name',
      render: (row) => row.name ?? '—',
    },
    {
      id: 'status',
      label: 'Status',
      width: 110,
      render: (row) => <Chip label={row.status} color={statusColorMap[row.status]} size="small" />,
    },
    {
      id: 'source',
      label: 'Source',
      width: 80,
      render: (row) => row.source,
    },
    {
      id: 'createdAt',
      label: 'Created',
      width: 110,
      render: (row) => formatDate(row.createdAt),
    },
    {
      id: 'createdBy',
      label: 'By',
      width: 120,
      render: (row) => row.createdByLabel,
    },
    {
      id: 'items',
      label: 'Items',
      width: 60,
      render: (row) => row.items.length,
    },
  ];

  return (
    <div data-testid="order-list" className="flex flex-col gap-2">
      <TextField
        size="small"
        placeholder="Search orders..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        fullWidth
      />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {STATUS_OPTIONS.map((status) => (
          <Chip
            key={status}
            label={status}
            size="small"
            variant={statusFilters.includes(status) ? 'filled' : 'outlined'}
            color={statusFilters.includes(status) ? statusColorMap[status] : 'default'}
            onClick={() => toggleStatus(status)}
          />
        ))}
      </Box>

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(row) => row.orderId}
        loading={loading}
        emptyMessage="No orders found"
        onRowClick={(row) => onSelectOrder(row.orderId)}
        selectedRowKey={selectedOrderId}
      />
    </div>
  );
}
